import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const REASONS = ['Damaged', 'Defective', 'Expired', 'Quality Reject', 'Other'];

const DisposeDialog = ({ open, onOpenChange, item, onDone }) => {
  const { api } = useAuth();
  const [form, setForm] = useState({ quantity: 1, reason: 'Damaged', batch_lot: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setForm({ quantity: 1, reason: 'Damaged', batch_lot: '', notes: '' });
  }, [open, item]);

  if (!item) return null;

  const printDisposalTicket = (ticket) => {
    const w = window.open('', '_blank', 'width=720,height=900');
    if (!w) return;
    const ts = ticket.disposed_at ? new Date(ticket.disposed_at).toLocaleString() : '';
    w.document.write(`<!doctype html><html><head><title>Disposal Ticket - ${ticket.item_sku}</title>
      <style>
        @page { size: 4in 6in; margin: 0.25in; }
        body { font-family: 'IBM Plex Sans', system-ui, sans-serif; color: #0f172a; margin: 0; padding: 16px; font-size: 10pt; }
        .head { border-bottom: 2px solid #dc2626; padding-bottom: 8px; margin-bottom: 12px; }
        .head h1 { margin: 0; font-size: 18pt; color: #dc2626; letter-spacing: 0.05em; }
        .head .sub { font-size: 9pt; color: #64748b; }
        .row { display: flex; justify-content: space-between; padding: 4px 0; border-bottom: 1px dashed #e2e8f0; }
        .label { font-size: 8pt; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; }
        .val { font-weight: 600; font-family: 'JetBrains Mono', ui-monospace, monospace; text-align: right; }
        .reason { background: #fef2f2; border: 1px solid #fecaca; padding: 8px; margin-top: 8px; border-radius: 2px; }
        .reason .r { font-weight: 700; color: #b91c1c; font-size: 12pt; }
        .signs { display: flex; gap: 16px; margin-top: 24px; }
        .sig { flex: 1; }
        .sig .line { border-top: 1px solid #0f172a; margin-top: 28px; padding-top: 4px; font-size: 8pt; color: #64748b; }
        .qty { font-size: 22pt; color: #dc2626; font-weight: 800; text-align: center; padding: 6px; border: 2px solid #dc2626; border-radius: 4px; margin: 6px 0; }
        @media print { body { padding: 0; } }
      </style></head><body>
        <div class="head">
          <h1>DISPOSAL TICKET</h1>
          <div class="sub">Ref: ${ticket.transaction_id}</div>
        </div>
        <div class="qty">${ticket.quantity} ${ticket.uom}</div>
        <div class="row"><span class="label">Item SKU</span><span class="val">${ticket.item_sku}</span></div>
        <div class="row"><span class="label">Item Name</span><span class="val">${ticket.item_name || ''}</span></div>
        <div class="row"><span class="label">Batch / Lot</span><span class="val">${ticket.batch_lot || '—'}</span></div>
        <div class="row"><span class="label">Disposed By</span><span class="val">${ticket.disposed_by}</span></div>
        <div class="row"><span class="label">Date / Time</span><span class="val">${ts}</span></div>
        <div class="reason">
          <div class="label">Reason</div>
          <div class="r">${ticket.reason}</div>
          ${ticket.notes ? `<div style="margin-top:6px; font-size:9pt; color:#475569; white-space:pre-wrap;">${ticket.notes}</div>` : ''}
        </div>
        <div class="signs">
          <div class="sig"><div class="line">Disposed by (signature)</div></div>
          <div class="sig"><div class="line">Witness / Supervisor</div></div>
        </div>
        <div style="margin-top:18px; font-size:8pt; color:#94a3b8; text-align:center;">
          Attach to disposed item / quarantine bin. Purchaser has been notified for follow-up.
        </div>
        <script>
          window.onload = function () {
            window.focus();
            window.print();
            window.onafterprint = function(){ window.close(); };
          };
        </script>
      </body></html>`);
    w.document.close();
  };

  const submit = async (e) => {
    e.preventDefault();
    const qty = parseFloat(form.quantity);
    if (!(qty > 0)) { toast.error('Quantity must be greater than 0'); return; }
    if (qty > (item.current_quantity || 0)) {
      if (!window.confirm(`Disposing more (${qty}) than on-hand (${item.current_quantity}). Continue?`)) return;
    }
    setSubmitting(true);
    try {
      const payload = {
        quantity: qty,
        reason: form.reason,
        batch_lot: form.batch_lot || null,
        notes: form.notes || null,
      };
      const { data } = await api().post(`/items/${item.id}/dispose`, payload);
      toast.success(`Disposed −${qty} ${item.base_uom}. New on-hand: ${data.new_quantity}`);
      if (data.ticket) printDisposalTicket(data.ticket);
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Disposal failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="dispose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" /> Mark for Disposal — {item.item_sku}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm">
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Item</div>
            <div className="text-slate-800 font-medium">{item.item_name}</div>
            <div className="text-xs text-slate-500">
              On hand: <span className="mono">{item.current_quantity} {item.base_uom}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Quantity *</Label>
              <Input
                type="number" min={1} step="any" required
                value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="rounded-sm mono"
                data-testid="dispose-quantity"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
                <SelectTrigger className="rounded-sm" data-testid="dispose-reason-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Batch / Lot # (if known)</Label>
            <Input
              value={form.batch_lot}
              onChange={(e) => setForm({ ...form, batch_lot: e.target.value })}
              placeholder="e.g. LOT-20260205"
              className="rounded-sm mono"
              data-testid="dispose-batch-lot"
            />
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Defect description, vendor reference, RMA, etc."
              className="rounded-sm"
              data-testid="dispose-notes"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="dispose-submit-btn">
              <Trash2 className="w-4 h-4 mr-2" /> {submitting ? 'Saving…' : 'Confirm Disposal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default DisposeDialog;
